import React, { useEffect, useState } from "react";
import LeaveManagementPage from "../../../../entities/DashboardPage/HRModule/LeaveManagementPage";
import AuthService from "../../../../api/AuthService";
import LoaderHelper from "../../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../../context/AlertContext";
import { getBlobErrorMessage } from "../../../../utils/blobError";

const LeaveManagement = () => {
  const { showAlert } = useAlert();
  const [permission, setPermission] = useState(null);
  const role = localStorage.getItem("role");
  const employeeId = localStorage.getItem("employeeId");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(1);
  const [reason, setReason] = useState("");
  const [leaveType, setLeaveType] = useState([]);
  const [employeeData, setEmployeeData] = useState();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState("");
  const [selectedLeaveType, setSelectedLeaveType] = useState("");

  const fetchPermissions = async () => {
    if ((role === "Employee" || role === "employee") && employeeId) {
      try {
        const permissionResult = await AuthService.getEmployeePermission(employeeId);
        if (permissionResult?.success && permissionResult?.data) {
          const permissions = permissionResult?.data;
          setPermission(permissions);
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    } else {
      const permissionObj = localStorage.getItem("permission");
      setPermission(permissionObj ? JSON.parse(permissionObj) : null);
    }
  };

  useEffect(() => {
    fetchPermissions();
    
    // Listen for permission updates
    const handlePermissionUpdate = () => {
      fetchPermissions();
    };
    
    window.addEventListener("permissionUpdated", handlePermissionUpdate);
    return () => {
      window.removeEventListener("permissionUpdated", handlePermissionUpdate);
    };
  }, []);

  useEffect(() => {
    if (permission !== null || role !== "Employee" && role !== "employee") {
      if (selectedLeaveType) {
        getLeaveBalanceLists(selectedLeaveType, page);
      } else {
        getLeaveManagementLists("", "", page);
      }
      getLeaveType();
    }
  }, [page, permission, selectedLeaveType])

  const getLeaveManagementLists = async (text, sort, page) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getLeaveManagementList(text, sort, page);
      if (result?.success) {
        let leaves = result?.data?.leaves || [];
        
        if ((role === "Employee" || role === "employee") && employeeId && permission) {
          const hasViewPermission = permission?.leave?.isView === true;
          const hasEditPermission = permission?.leave?.isEdit === true;
          
          if (hasEditPermission) {
            setTotal(result?.data?.total);
          } else if (hasViewPermission) {
            leaves = leaves.filter((leave) => leave?.employee?._id === employeeId || leave?.employee?.userId === employeeId);
            setTotal(leaves.length);
          } else {
            leaves = [];
            setTotal(0);
          }
        } else {
          setTotal(result?.data?.total);
        }
        
        setData(leaves);
        setFilteredData(leaves);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getLeaveBalanceLists = async (leaveTypeValue, pageValue = 1) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getLeaveBalanceList(leaveTypeValue, pageValue);
      if (result?.success) {
        const leaves = result?.data?.employees || [];
        setTotal(result?.data?.total || leaves.length || 0);
        setData(leaves);
        setFilteredData(leaves);
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const SearchEmployee = async (text) => {
    setLoading(true);
    try {
      const result = await AuthService.getEmployeeListForLeave(text);
      if (result?.success) {
        setEmployeeData(result?.data?.employees);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };
  const getLeaveType = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAccountLeaveType();
      if (result?.success) {
        setLeaveType(result?.data);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleLeaveApply = async (leaveType, leaveFrom, leaveTo, days, tz, id, reason) => {
    setLoading(true);
    try {
      const result = await AuthService.handleApplyRequest(leaveType, leaveFrom, leaveTo, days, tz, id, reason);
      if (result?.success) {
        showAlert(result?.message, "success");
        setOpen(false);
        setReason("");
        if (selectedLeaveType) {
          getLeaveBalanceLists(selectedLeaveType, page);
        } else {
          getLeaveManagementLists(searchText, sort, page);
        }
        setLoading(false);
      } else {
        setLoading(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      setLoading(false);
    }
  }

  const handleLeaveUpdate = async (leaveId, leaveType, leaveFrom, leaveTo, days, tz, reason) => {
    setLoading(true);
    try {
      const result = await AuthService.handleUpdateLeaveRequest(leaveId, leaveType, leaveFrom, leaveTo, days, tz, reason);
      if (result?.success) {
        showAlert(result?.message, "success");
        setOpen(false);
        setReason("");
        if (selectedLeaveType) {
          getLeaveBalanceLists(selectedLeaveType, page);
        } else {
          getLeaveManagementLists(searchText, sort, page);
        }
      } else {
        showAlert(result?.message || "Unable to update leave request.", "error");
      }
    } catch (error) {
      showAlert("Unable to update leave request.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (text) => {
    setSearchText(text);
    if (selectedLeaveType) {
      setPage(1);
      getLeaveBalanceLists(selectedLeaveType, 1);
    } else {
      getLeaveManagementLists(text, sort, page);
    }
  };

  const handleSort = (sortOption) => {
    setSort(sortOption);
    if (selectedLeaveType) {
      getLeaveBalanceLists(selectedLeaveType, page);
    } else {
      getLeaveManagementLists(searchText, sortOption, page);
    }
  };

  const handleLeaveTypeChange = (value) => {
    setSelectedLeaveType(value);
    setPage(1);
    if (value) {
      getLeaveBalanceLists(value, 1);
    } else {
      getLeaveManagementLists(searchText, sort, 1);
    }
  };

  const handleDownload = async () => {
    LoaderHelper.loaderStatus(true);

    setLoading(true)
    try {
      const result = await AuthService.leaveDownload();
      if (!result) {
        setLoading(false);
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      } else {
        const downloadError = await getBlobErrorMessage(result);
        if (downloadError) {
          setLoading(false);
          LoaderHelper.loaderStatus(false);
          showAlert(downloadError, "error");
          return;
        }
        const blob = new Blob([result], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `FileName.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        setLoading(false);
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleCompanyChange = () => {
    if (selectedLeaveType) {
      getLeaveBalanceLists(selectedLeaveType, page);
    } else {
      getLeaveManagementLists(searchText, sort, page);
    }
  };

  const handleManageLeave = async (action, employeeId, leaveType, numberOfDays, notes) => {
    setLoading(true);
    try {
      const payload = {
        employee: employeeId,
        leave_type: leaveType,
        numberOfDays: Number(numberOfDays),
        notes: notes || null,
      };
      
      const result = await AuthService.manageLeaveBalance(action, payload);
      if (result?.success) {
        showAlert(result?.message || "Leave balance updated successfully", "success");
        if (selectedLeaveType) {
          getLeaveBalanceLists(selectedLeaveType, page);
        } else {
          getLeaveManagementLists(searchText, sort, page);
        }
        setLoading(false);
        return true;
      } else {
        showAlert(result?.message || "Failed to update leave balance", "error");
        setLoading(false);
        return false;
      }
    } catch (error) {
      showAlert(error?.message || "Failed to update leave balance", "error");
      setLoading(false);
      return false;
    }
  };

  const handleDeleteLeave = async (id) => {
    if (!id) return;
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteLeaveRequest(id);
      if (result?.success) {
        showAlert(result?.message || "Leave deleted successfully", "success");
        getLeaveManagementLists(searchText, sort, page);
      } else {
        showAlert(result?.message || "Failed to delete leave", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Failed to delete leave", "error");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleLeaveRequest = async (id, status) => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await AuthService.handleLeaveRequest(id, status, false);
      if (result?.success) {
        showAlert(result?.message || `Leave ${status.toLowerCase()} successfully`, "success");
        setOpen(false);
        if (selectedLeaveType) {
          getLeaveBalanceLists(selectedLeaveType, page);
        } else {
          getLeaveManagementLists(searchText, sort, page);
        }
      } else {
        showAlert(result?.message || `Failed to ${status.toLowerCase()} leave`, "error");
      }
    } catch (error) {
      showAlert(error?.message || `Failed to ${status.toLowerCase()} leave`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LeaveManagementPage
      handleDownload={handleDownload}
      role={role}
      reason={reason}
      setReason={setReason}
      permission={permission}
      page={page}
      total={total}
      setPage={setPage}
      handleEmployee={SearchEmployee}
      loading={loading}
      employeeData={employeeData}
      data={filteredData.length > 0 ? filteredData : data}
      open={open}
      setOpen={setOpen}
      getLeaveType={leaveType}
      handleLeaveApply={handleLeaveApply}
      handleLeaveUpdate={handleLeaveUpdate}
      handleFilter={handleFilter}
      handleSort={handleSort}
      getLeaveManagementLists={getLeaveManagementLists}
      onCompanyChange={handleCompanyChange}
      handleManageLeave={handleManageLeave}
      handleDeleteLeave={handleDeleteLeave}
      handleLeaveRequest={handleLeaveRequest}
      selectedLeaveType={selectedLeaveType}
      handleLeaveTypeChange={handleLeaveTypeChange}
      setSelectedLeaveType={setSelectedLeaveType}
    />
  )

}

export default LeaveManagement;
